<template>
  <container>
    <div class="er-page">
      <div class="pg-title-row">
        <div>
          <h1 class="pg-title">Update Race</h1>
          <p class="pg-title-sub">Edit race details, stream settings, media and trainer ownership</p>
        </div>
        <router-link to="/admin/races" class="er-ghost-btn">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round">
            <path d="M19 12H5"/>
            <path d="M12 19l-7-7 7-7"/>
          </svg>
          Races
        </router-link>
      </div>

      <div v-if="!update" class="er-loading-stack">
        <section v-for="n in 4" :key="'er-sk-' + n" class="er-card">
          <div class="er-card-header">
            <span class="er-icon er-icon--loading sk-shimmer"></span>
            <div class="er-skeleton-head">
              <div class="er-sk-title sk-shimmer"></div>
              <div class="er-sk-sub sk-shimmer"></div>
            </div>
          </div>
          <div class="er-grid er-grid--two">
            <div class="er-sk-input sk-shimmer"></div>
            <div class="er-sk-input sk-shimmer"></div>
          </div>
        </section>
      </div>

      <custom-form v-else :handle-submit="handleSubmit" class="er-form">
        <section class="er-card">
          <div class="er-card-header">
            <span class="er-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                <path d="M8 6h13"/>
                <path d="M8 12h13"/>
                <path d="M8 18h13"/>
                <path d="M3 6h.01"/>
                <path d="M3 12h.01"/>
                <path d="M3 18h.01"/>
              </svg>
            </span>
            <div>
              <h2 class="er-card-title">Race Details</h2>
              <p class="er-card-sub">Edit the core schedule, description and participant capacity</p>
            </div>
          </div>

          <div class="er-grid er-grid--two">
            <text-field class="er-field" v-model="update.name" label="Name" name="name" v-validate="'required'" data-vv-value-path="input" data-vv-name="name" error-message="Fill in the name" :has-error="errors.has('name')" :required="true"></text-field>

            <date-time-field class="er-field" v-model="update.date" :label="`Date - ${getTimezone()}`" name="date" v-validate="'required'" data-vv-value-path="startDate.time" data-vv-name="date" error-message="Fill in the date" :has-error="errors.has('date')" :required="true"></date-time-field>

            <div class="form__group er-field er-field--full">
              <label for="description">Description <span class="required">*</span></label>
              <textarea v-validate data-vv-rules="required" :class="{'form-control': true, 'is-danger': errors.has('description') }" name="description" rows="6" v-model="update.description" id="txtArea" @keydown.enter.prevent="onDescriptionChange" required></textarea>
              <span v-show="errors.has('description')" class="help is-danger">Fill in the description</span>
            </div>

            <div class="form__group er-field">
              <label for="duration">Duration in minutes <span class="required">*</span></label>
              <input v-validate="'required'" :class="{'form-control': true, 'is-danger': errors.has('duration') }" type="number" step="1" :min="1" :max="max_duration" name="duration" v-model.number="update.duration" required>
              <span v-show="errors.has('duration')" class="help is-danger">Fill in the duration</span>
            </div>

            <div class="form__group er-field">
              <label for="rsvp_limit">RSVP Limit <span class="required">*</span></label>
              <input v-validate="'required'" :class="{'form-control': true, 'is-danger': errors.has('rsvp_limit') }" type="number" step="1" :min="1" :max="max_rsvp_limit" name="rsvp_limit" v-model.number="update.rsvp_limit" required>
              <span v-show="errors.has('rsvp_limit')" class="help is-danger">Fill in the RSVP limit</span>
            </div>
          </div>
        </section>

        <section class="er-card">
          <div class="er-card-header">
            <span class="er-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                <path d="M4 4h16v16H4z"/>
                <path d="M4 9h16"/>
                <path d="M9 20V9"/>
              </svg>
            </span>
            <div>
              <h2 class="er-card-title">Type And Stream</h2>
              <p class="er-card-sub">Update medal behavior, categories, language and audio routing</p>
            </div>
          </div>

          <div class="er-grid er-grid--two">
            <div class="form__group er-field">
              <label for="type">First Type</label>
              <select class="er-select" v-model="update.type" label="First Type" name="type">
                <option value="LIVE">Live (Medal)</option>
                <option value="NO_MEDAL">No Medal</option>
                <option value="ON_DEMAND_MEDAL">On demand (Medal)</option>
                <option value="ON_DEMAND_NO_MEDAL">On demand (No Medal)</option>
              </select>
            </div>

            <race-media v-if="update.type == 'ON_DEMAND_MEDAL' || update.type == 'ON_DEMAND_NO_MEDAL'" class="er-field" @saved="loadAudio" v-model="update.audioFileUrl" label="On demand File" name="media" data-vv-value-path="input" data-vv-name="media" error-message="Fill in the audio" :has-error="errors.has('media')" :required="false"></race-media>

            <div v-if="(update.type == 'ON_DEMAND_MEDAL' || update.type == 'ON_DEMAND_NO_MEDAL') && update.audioFileUrl" class="er-audio-box er-field er-field--full">
              <span class="er-preview-label">Current on demand file</span>
              <audio id="ondemand-audio" controls class="er-audio">
                <source :src="update.audioFileUrl" type="audio/mp3">
              </audio>
            </div>

            <div class="form__group er-field">
              <label for="secondType">Second Type</label>
              <div v-if="raceTypesLoading" class="er-sk-input sk-shimmer"></div>
              <select v-else class="er-select" v-model="update.secondType" label="Type" name="type">
                <option v-for="type in raceTypes" :key="type.name" v-bind:value="type.name">{{ type.englishText }}</option>
              </select>
            </div>

            <div class="form__group er-field">
              <label for="thirdType">Third Type</label>
              <select class="er-select" v-model="update.thirdType" label="Type" name="type">
                <option value="">NONE</option>
                <option value="NO_MEDAL">NO MEDAL</option>
                <option value="PromoL">L</option>
                <option value="PromoI">I</option>
                <option value="PromoV">V</option>
                <option value="PromoE">E</option>
                <option value="LIVE_60MIN">60MIN_MEDAL</option>
                <option value="PromoU">U</option>
                <option value="PromoR">R</option>
                <option value="MEDAL_20K">20Km Medal</option>
              </select>
            </div>

            <div class="form__group er-field">
              <label for="channel">Channel</label>
              <div v-if="channelsLoading" class="er-sk-input sk-shimmer"></div>
              <select v-else class="er-select" v-model="update.mobileChannelToPlay" label="Channel" name="channel">
                <option :value="channels.audioChannelA">A</option>
                <option :value="channels.audioChannelB">B</option>
                <option :value="channels.audioChannelC">C</option>
                <option :value="channels.audioChannelD">D</option>
              </select>
            </div>

            <div class="form__group er-field">
              <label for="language">Language</label>
              <select class="er-select" v-model="update.language" label="Language" name="language">
                <option v-for="locale in locales" :key="locale.id" v-bind:value="locale.id">{{ locale.name }}</option>
              </select>
            </div>
          </div>
        </section>

        <section class="er-card">
          <div class="er-card-header">
            <span class="er-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                <path d="M10 13a5 5 0 0 0 7.07 0l2.83-2.83a5 5 0 0 0-7.07-7.07L11.5 4.43"/>
                <path d="M14 11a5 5 0 0 0-7.07 0L4.1 13.83a5 5 0 0 0 7.07 7.07l1.33-1.33"/>
              </svg>
            </span>
            <div>
              <h2 class="er-card-title">Action Link</h2>
              <p class="er-card-sub">Update the end-session URL and optional circular action image</p>
            </div>
          </div>

          <div class="er-grid er-grid--two">
            <div class="er-field">
              <label class="er-label" for="endSessionUrl">Action URL</label>
              <text-field v-model="update.endSessionUrl" name="endSessionUrl" data-vv-value-path="input" data-vv-name="name"></text-field>
            </div>

            <race-photo class="er-field" v-model="update.actionImageUrl" label="Action Image" name="actionImage" data-vv-value-path="input" data-vv-name="actionImage" error-message="Fill in the photo with size below 200 Kb" :has-error="errors.has('actionImage')" :required="false"></race-photo>

            <div v-if="update.actionImageUrl != ''" class="er-action-preview er-field er-field--full">
              <div>
                <span class="er-preview-label">Current action image</span>
                <img :src="update.actionImageUrl" class="er-action-img" alt="Current action image">
              </div>
              <button type="button" class="er-icon-btn er-icon-btn--delete" @click="removeActionImg" title="Remove action image">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M3 6h18"/>
                  <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                  <path d="M10 11v6M14 11v6"/>
                </svg>
              </button>
            </div>
          </div>
        </section>

        <section class="er-card">
          <div class="er-card-header">
            <span class="er-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
                <path d="M7 7h.01"/>
              </svg>
            </span>
            <div>
              <h2 class="er-card-title">Tags And Trainer</h2>
              <p class="er-card-sub">Update race tags and the trainer assigned to this session</p>
            </div>
          </div>

          <div class="er-grid er-grid--two">
            <div class="form__group er-field race-tags">
              <label for="tags">Tags</label>
              <div v-if="tagsLoading" class="er-sk-input sk-shimmer"></div>
              <selectize v-else v-bind:options="tagsOptions" v-model="selectedTags" multiple size="1" :isRunnerTag="false" :isRaceTag="true" :raceId="this.$route.params.raceId" data-dropdown-parent="body" data-max-options="100" data-value-field="id" data-label-field="name" data-search-field="name" data-sort-field="name" class="er-selectize-hidden"></selectize>
            </div>

            <div class="form__group er-field">
              <label for="Trainer">Trainer</label>
              <div v-if="trainerLoading" class="er-sk-input sk-shimmer"></div>
              <select v-else v-model="trainerId" label="Trainer" name="Trainer" @change="changeTrainer" class="er-select trainer-list">
                <option v-for="(trainer, index) in trainerList" v-bind:value="trainer.id" :key="index">{{ trainer.firstName }}</option>
              </select>
            </div>
          </div>
        </section>

        <section class="er-card">
          <div class="er-card-header">
            <span class="er-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                <circle cx="8.5" cy="8.5" r="1.5"/>
                <polyline points="21 15 16 10 5 21"/>
              </svg>
            </span>
            <div>
              <h2 class="er-card-title">Race Photo</h2>
              <p class="er-card-sub">Update the image shown with this race in the mobile app</p>
            </div>
          </div>

          <div class="er-photo-grid">
            <div v-if="update.imageUrl != ''" class="er-photo-preview">
              <span class="er-preview-label">Current race photo</span>
              <img :src="update.imageUrl" class="er-photo-img" alt="Current race photo">
            </div>
            <upload-photo class="er-field" v-model="update.imageUrl" label="Photo" name="photo" v-validate="'required'" data-vv-value-path="input" data-vv-name="photo" error-message="Fill in the photo with size below 200 Kb" :has-error="errors.has('photo')" :required="true"></upload-photo>
          </div>
        </section>

        <div class="er-actions">
          <router-link to="/admin/races" class="er-ghost-btn">
            Cancel
          </router-link>
          <button type="submit" class="er-primary-btn" id="submit-form" :disabled="isSubmitting">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round">
              <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
              <polyline points="17 21 17 13 7 13 7 21"/>
              <polyline points="7 3 7 8 15 8"/>
            </svg>
            {{ isSubmitting ? 'Updating...' : 'Update Race' }}
          </button>
        </div>
      </custom-form>
    </div>
  </container>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import Container from '@/components/Container'
import CustomForm from '@/components/Forms/Form'
import TextField from '@/components/Forms/TextField'
import DateTimeField from '@/components/Forms/DateTimeField'
import RacePhoto from '@/components/Forms/RacePhoto'
import UploadPhoto from '@/components/Forms/UploadPhoto'
import RaceMedia from '@/components/Forms/RaceMedia'
import {pick} from 'ramda'
import moment from 'moment'
import SettingsService from "@/services/settings"
import TrainerService from '@/services/trainer'
import Selectize from '@/components/Selectize'
import TagService from '../../services/tag'
import RaceService from '../../services/races'
import { db } from '../../firebase'
export default {
  name: 'UpdateRace',
  data: () => ({
    update: null,
    upload: null,
    locales: [ {id: 'en', name: 'English'}, {id: 'ja', name: 'Japanese'}],
    types: [],
    channels: {},
    tagsOptions: [],
    selectedTags: [],
    trainerList: [],
    trainerId: '',
    raceTrainer: '',
    raceTypes: [],
    isSubmitting: false,
    channelsLoading: true,
    tagsLoading: true,
    trainerLoading: true,
    raceTypesLoading: true,
    max_duration: 1440,
    max_rsvp_limit: 10000
  }),
  computed: mapGetters({
    trainer: 'Trainer/trainer',
    race: 'Race/race'
  }),
  methods: {
    initialize () {
      this.getRaceTypes().then(res => {
        if (res && res.data) {
          res.data.unshift('NONE')
          this.types = res.data
        }
      }).catch(error => {
        console.error('Error getting race types:', error)
      })

      this.getRace(this.$route.params.raceId)
      this.getAllTags()
      this.getTrainerList()
      this.getRaceTypeAvailable()
    },
    getRaceTypeAvailable () {
      this.raceTypesLoading = true
      RaceService.getRaceTypeAvailable().then(response => {
        if (response.data && response.data != undefined && response.data != '') {
          let allRaceTypes = response.data
          //Remove these from the second type (https://app.asana.com/0/1137425856170315/1200251895400268)
          let removeItem = ["SOLO", "PromoL", "PromoI", "PromoV", "PromoE", "PromoU", "PromoR", "NO_MEDAL", "LIVE_60MIN", "ON_DEMAND_MEDAL", "ON_DEMAND_NO_MEDAL", "WALK"]
          _.remove(allRaceTypes, obj => removeItem.includes(obj.name))
          // var addElements = allRaceTypes.push({englishText: 'PromoU', name: 'PromoU', japaneseText: 'PromoU'}, {englishText: 'PromoR', name: 'PromoR', japaneseText: 'PromoR'})
          this.raceTypes = allRaceTypes

          //this.raceTypes = response.data
        }
      }).catch(error => {
        console.log("Error getting race type--->>", error)
      }).finally(() => {
        this.raceTypesLoading = false
      })
    },
    getAllTags () {
      this.tagsLoading = true
      TagService.getAllTags().then((response) => {
        console.log("response----", response)
        this.tagsOptions = response.data
        return this.getRaceTags()
      }).finally(() => {
        this.tagsLoading = false
      })
    },
    getRaceTags () {
      return TagService.getRaceTags(this.$route.params.raceId).then((response) => {
        //console.log("this.options---",this.options.map(a => a.id))
        this.selectedTags = response.data.map(a => a.id)
      })
    },
    getTrainerList () {
      this.trainerLoading = true
      TrainerService.getAll().then(response => {
        if (response.data && response.data != undefined && response.data != '') {
          this.trainerList = response.data
        }
      }).catch(error => {
        console.log("Error get all trainer--->>", error)
      }).finally(() => {
        this.trainerLoading = false
      })
    },
    changeTrainer () {
      // CHANGE THE TRAINER OF SESSION
      TrainerService.getTrainer(this.trainerId).then(response => {
        this.raceTrainer = response.data
      }).catch(error => {
        console.log("Error Get trainer by id--->>", error)
      })
    },
    loadAudio () {
      const audio = document.getElementById("ondemand-audio")
      if (audio) {
        audio.load()
      }
    },
    handleSubmit () {
      this.$validator.validateAll()
        .then(success => {
          if (!success) {
            return
          }
          this.isSubmitting = true

          try {
            // Update Firebase data
            const firebaseUpdate = db.ref(`/${this.$fbDbEntry}/${this.$route.params.raceId}`).update({
              'streamingUrl': this.update.mobileChannelToPlay,
              'endSessionUrl': this.update.endSessionUrl
            }).catch(error => {
              console.error("Firebase error--", error)
            })

            // Set thirdtype null
            if (this.update.thirdType === '') {
              this.update.thirdType = null
            }

            // Update trainer
            this.update.trainer = this.raceTrainer // CHANGE THE TRAINER OF SESSION

            // Wait for Firebase update to complete before updating race
            Promise.resolve(firebaseUpdate).then(() => {
              // Make a copy of the update object to avoid mutation issues
              const raceData = JSON.parse(JSON.stringify(this.update))

              // Ensure the race ID is properly included
              if (!raceData.id) {
                console.error('Race ID is missing in update data')
                throw new Error('Race ID is required for update')
              }

              return this.updateRace(raceData)
            })
            .then(response => {
              this.$router.push({ path: '/admin/races' })
              this.error = ''
              this.isSubmitting = false
            })
            .catch(error => {
              console.error('Failed to update race:', error)
              this.error = 'Failed!'
              this.isSubmitting = false
            })
          } catch (error) {
            console.error('Error in handleSubmit:', error)
            this.error = 'An unexpected error occurred'
            this.isSubmitting = false
          }
        })
    },
    ...mapActions({
      updateRace: 'Race/updateOne',
      getRace: 'Race/get',
      getRaceTypes: 'Race/getTypes'
    }),
    getTimezone () {
      return new Date().toString().match(/([A-Z]+[+-][0-9]+.*)/)[1]
    },
    onDescriptionChange () {
      this.update.description = `${this.update.description}\n*`
    },
    async getExistingChannels () {
      this.channelsLoading = true
      try {
        const channels = await SettingsService.get()
        this.channels = channels.data
        //this.update.mobileChannelToPlay = channels.data.audioChannelA
        return channels.data
      } finally {
        this.channelsLoading = false
      }
    },
    removeActionImg () {
      this.update.actionImageUrl = ''
    }
  },
  created () {
    this.initialize()
    this.getExistingChannels()
  },
  watch: {
    race () {
      console.log("this.race---", this.race)
      const race = pick(['id', 'name', 'description', 'mobileChannelToPlay', 'date', 'startDate', 'finishDate', 'type', 'secondType', 'thirdType', 'language', 'notes', 'imageUrl', 'audioFileUrl', 'schedule', 'trainer', 'duration', 'rsvp_limit', 'endSessionUrl', 'actionImageUrl'], this.race)
      console.log("race---", race)
      var stillUtc = moment.utc(race.date).toDate()
      var local = moment(stillUtc).local().format('YYYY-MM-DD HH:mm:ss')
      race.date = local
      console.log(race.endSessionUrl)
      if (race.endSessionUrl == undefined) { race.endSessionUrl = '' }
      this.update = race
      this.trainerId = race.trainer.id
      this.raceTrainer = race.trainer
      //set thirdtype null
      if (this.update.thirdType == null) {
        this.update.thirdType = ''
      }
      // Clear any eager-validation errors that accumulated while fields
      // were mounted against empty values (before API data arrived).
      this.$nextTick(function () {
        this.$validator.reset()
      })
    }
  },
  components: {
    Container,
    CustomForm,
    TextField,
    DateTimeField,
    RacePhoto,
    UploadPhoto,
    RaceMedia,
    Selectize
  }
}
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;900&display=swap');

@keyframes sk-shimmer {
  0% { background-position: -600px 0; }
  100% { background-position: 600px 0; }
}

.sk-shimmer {
  background: linear-gradient(90deg, #f0f0f5 25%, #e4e4ef 50%, #f0f0f5 75%);
  background-size: 600px 100%;
  animation: sk-shimmer 1.4s ease-in-out infinite;
  border-radius: 6px;
}

.pg-title-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #f0f0f5;
  margin-bottom: 1.5rem;
}

.pg-title {
  font-family: 'Inter', sans-serif;
  font-size: 34px;
  font-weight: 900;
  letter-spacing: -0.04em;
  color: #1a1a2e;
  line-height: 1.1;
  margin: 0;
}

.pg-title-sub {
  font-size: 13px;
  color: #8a8a9a;
  font-weight: 400;
  margin: 5px 0 0;
}

.er-page {
  padding-bottom: 2rem;
}

.er-form.form-container,
.er-loading-stack {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 40px;
}

.er-card {
  border: 1px solid #e8e8ed;
  border-radius: 14px;
  padding: 1.5rem 1.75rem;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  background: #fff;
}

.er-card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 1.25rem;
}

.er-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #f0effe;
  color: #5b4fcf;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 36px;
}

.er-icon--loading {
  background: #f0f0f5;
}

.er-card-title {
  font-size: 16px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0;
  line-height: 1.2;
}

.er-card-sub {
  font-size: 12px;
  color: #8a8a9a;
  margin: 4px 0 0;
  line-height: 1.35;
}

.er-grid,
.er-photo-grid {
  display: grid;
  gap: 1rem 1.25rem;
}

.er-grid--two,
.er-photo-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.er-field--full {
  grid-column: 1 / -1;
}

.er-page .form__group,
.er-field {
  margin-bottom: 0;
  min-width: 0;
}

.er-page .form__group > label,
.er-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #1a1a2e;
  margin-bottom: 7px;
}

.er-page .required {
  color: #c0392b;
}

.er-page .form-control,
.er-select,
.er-page textarea.form-control {
  width: 100%;
  min-height: 36px;
  border: 1px solid #e8e8ed;
  border-radius: 8px;
  background: #fafafa;
  color: #1a1a2e;
  font-size: 13px;
  line-height: 1.35;
  padding: 0 12px;
  box-shadow: none;
}

.er-page .datepickbox,
.er-page .cov-vue-date {
  width: 100%;
}

.er-page .cov-datepicker {
  width: 100% !important;
  min-height: 36px !important;
  border: 1px solid #e8e8ed !important;
  border-radius: 8px !important;
  background: #fafafa !important;
  color: #1a1a2e !important;
  font-size: 13px !important;
  line-height: 1.35 !important;
  padding: 0 12px !important;
  box-shadow: none !important;
}

.er-page textarea.form-control {
  min-height: 118px;
  padding: 10px 12px;
  resize: vertical;
}

.er-page .form-control:focus,
.er-select:focus,
.er-page textarea.form-control:focus {
  outline: none;
  border-color: #5b4fcf;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(91, 79, 207, 0.1);
}

.er-page .is-danger {
  border-color: #c0392b;
}

.er-page .help.is-danger {
  display: block;
  font-size: 12px;
  color: #c0392b;
  margin-top: 6px;
}

.er-select {
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg width='13' height='13' viewBox='0 0 24 24' fill='none' stroke='%238a8a9a' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 11px center;
  padding-right: 34px;
}

.er-sk-input {
  width: 100%;
  height: 36px;
}

.er-skeleton-head {
  flex: 1;
}

.er-sk-title {
  width: 140px;
  height: 16px;
  margin-bottom: 7px;
}

.er-sk-sub {
  width: 260px;
  max-width: 100%;
  height: 12px;
}

.er-page .race-tags .selectize-control {
  width: 100%;
}

.er-page .race-tags .selectize-control.multi .selectize-input {
  min-height: 36px;
  border: 1px solid #e8e8ed;
  border-radius: 8px;
  background: #fafafa;
  box-shadow: none;
  padding: 5px 8px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  box-sizing: border-box;
}

.er-page .race-tags .selectize-control.multi .selectize-input.focus {
  border-color: #5b4fcf;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(91, 79, 207, 0.1);
}

.er-page .race-tags .selectize-control.multi .selectize-input > div.active {
  background: #e5e2ff;
  color: #413b94;
  border: 0 solid transparent;
}

.er-page .race-tags .selectize-control.multi .selectize-input > div {
  max-width: 100%;
  min-height: 26px;
  border-radius: 999px;
  background: #f3f1ff;
  color: #413b94;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  margin: 0;
  padding: 4px 6px 4px 10px;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.2;
  box-sizing: border-box;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.er-page .race-tags .selectize-control.plugin-remove_button .selectize-input > div .remove {
  width: 18px;
  height: 18px;
  border-left: 0;
  border-radius: 50%;
  color: #6b5fd6;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 18px;
  margin: 0 0 0 2px;
  padding: 0;
  position: static;
  font-size: 15px;
  font-weight: 700;
  line-height: 1;
  text-decoration: none;
  opacity: 0.85;
}

.er-page .race-tags .selectize-control.plugin-remove_button .selectize-input > div .remove:hover {
  background: #e5e2ff;
  color: #4338ca;
  opacity: 1;
}

.er-page .race-tags .selectize-input > input {
  font-size: 13px;
  color: #1a1a2e;
  min-width: 120px;
}

.er-audio-box,
.er-action-preview,
.er-photo-preview {
  border: 1px solid #e8e8ed;
  border-radius: 10px;
  background: #fafafa;
  padding: 12px;
}

.er-audio {
  width: 100%;
  height: 36px;
  display: block;
}

.er-action-preview {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.er-preview-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #8a8a9a;
  margin-bottom: 8px;
}

.er-action-img {
  width: 82px;
  height: 82px;
  border-radius: 50%;
  border: 2px solid #e8e8ed;
  object-fit: cover;
  display: block;
}

.er-photo-img {
  width: 100%;
  max-height: 180px;
  border-radius: 8px;
  object-fit: cover;
  display: block;
}

.er-icon-btn {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid #e8e8ed;
  background: #fff;
  color: #8a8a9a;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.er-icon-btn--delete:hover {
  color: #c0392b;
  border-color: #f2c1bb;
  background: #fff7f6;
}

.er-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  padding-top: 0.5rem;
}

.er-primary-btn,
.er-ghost-btn {
  height: 36px;
  min-height: 36px;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  border-radius: 8px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  line-height: 1;
  margin: 0;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.18s ease;
  vertical-align: middle;
  align-self: center;
}

.er-form.form-container .er-actions .er-primary-btn,
.er-form.form-container .er-actions .er-ghost-btn {
  margin: 0;
}

.er-primary-btn {
  appearance: none;
  -webkit-appearance: none;
  background: #5b4fcf;
  color: #fff;
  border: 1.5px solid transparent;
  padding: 0 18px;
}

.er-primary-btn:hover {
  background: #4338ca;
}

.er-primary-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.er-ghost-btn {
  border: 1.5px solid #5b4fcf;
  background: #fff;
  color: #5b4fcf;
  min-width: 82px;
  padding: 0 14px;
}

.er-ghost-btn:hover {
  background: #f0effe;
}

@media (max-width: 760px) {
  .pg-title-row {
    align-items: flex-start;
    flex-direction: column;
  }

  .er-grid--two,
  .er-photo-grid {
    grid-template-columns: 1fr;
  }

  .er-card {
    padding: 1.25rem;
  }

  .er-action-preview {
    align-items: flex-start;
  }

  .er-actions {
    flex-direction: column-reverse;
    align-items: stretch;
  }

  .er-primary-btn,
  .er-ghost-btn {
    width: 100%;
  }
}
</style>



// WEBPACK FOOTER //
// Update.vue?03b6e263